import Button from '@/common/components/Button'
import Modal from '@/common/components/Modal'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import TextArea from '@/common/components/TextArea'
import { FormTransformers } from '@/common/utils/formTransformers'
import { useReviewPaymentAdvance } from '@/features/paymentAdvances/hooks/useReviewPaymentAdvance'
import {
  rejectPaymentAdvanceSchema,
  RejectPaymentAdvanceValidationSchema,
} from '@/features/paymentAdvances/schemas/reject.schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

interface PaymentAdvanceRejectModalProps {
  paymentAdvanceId?: string
  disabled: boolean
}

export const PaymentAdvanceRejectModal = ({ paymentAdvanceId, disabled }: PaymentAdvanceRejectModalProps) => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<string[] | boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)

  const { mutate: reviewPaymentAdvance, isPending: reviewPaymentAdvanceIsPending } = useReviewPaymentAdvance({
    onSuccess: () => {
      setIsError(false)
      setMessages(['Adiantamento rejeitado com sucesso!'])
    },
    onError: (error) => {
      if (error.response?.data.errors.base) {
        setIsError(true)
        setMessages(error.response?.data.errors.base)
      } else {
        FormTransformers.errorsTransformer({ setError })(error)
      }
    },
  })

  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = useForm<RejectPaymentAdvanceValidationSchema>({
    resolver: zodResolver(rejectPaymentAdvanceSchema),
    defaultValues: {
      reject_reason: '',
    },
  })

  const onSubmit: SubmitHandler<RejectPaymentAdvanceValidationSchema> = (data) => {
    reviewPaymentAdvance({ id: paymentAdvanceId || '', state: 'reject', reject_reason: data.reject_reason })
  }

  const onClose = () => {
    setIsOpen(false)
    queryClient.invalidateQueries({ queryKey: ['paymentAdvance', paymentAdvanceId] })
    queryClient.invalidateQueries({ queryKey: ['paymentAdvances'] })
    navigate('/payment-advances')
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={(value) => {
          setIsOpen(value)
          reset()
        }}
        Trigger={<Button variant="outline-error">Rejeitar</Button>}
        triggerDisabled={disabled}
        title="Rejeitar adiantamento"
        useCancellableFooter
        confirmButtonText="Confirmar rejeição"
        onConfirm={handleSubmit(onSubmit)}
        disabled={reviewPaymentAdvanceIsPending}
        confirmButtonIsLoading={reviewPaymentAdvanceIsPending}
        body={
          <form className="flex flex-col gap-4">
            <TextArea
              label="Motivo da rejeição do adiantamento"
              markAsRequired
              maxLength={64}
              {...register('reject_reason')}
              errorMessage={errors.reject_reason?.message || undefined}
              variant={errors.reject_reason?.message ? 'error' : 'default'}
            />
          </form>
        }
      />

      {messages && (
        <ModalMessageSimple messages={messages} setMessages={setMessages} isError={isError} onClose={onClose} />
      )}
    </>
  )
}
